import { useState, useEffect, useMemo, useRef } from "react";
import { SelectedAppleFeature } from "../../../../../../../Component/Map/Layers/apple-count/calibration/AppleCountCalibrationLayer";
import {
  calculateAppleThresholds,
  findBucket,
  YieldBucket,
} from "../../../../../../../Component/Map/Layers/apple-count/apple-count-helper";
import {
  CALIBRATION_COLOR_COUNT,
  calibrationColorVar,
} from "../../../../../../../Component/Map/Layers/apple-count/calibration/calibration-colors";
import {
  createCalibrationSet,
  getCalibrationMeasurementSet,
  getYieldEstimate,
  YieldEstimate,
} from "../../../../../../../API/calibration";
import {
  AppleCountCalibrationFeatureCollection,
  CalibrationItem,
} from "./Calibration.types";
import {
  CalibrationDraftMeasurement,
  clearCalibrationDraft,
  isDraftStale,
  pruneCalibrationDrafts,
  readCalibrationDraft,
  writeCalibrationDraft,
} from "./calibration-draft";
import { CalibrationData } from "../../../../../../../API/fields";
import { useTranslation } from "react-i18next";
import { readSubmitOutcome, SubmitOutcome } from "./status/calibration-outcome";
import { failureMessageKey } from "../../../../../../../Component/CalibrationBanners/failure-message";

export type SubmitNotice = {
  severity: "error" | "success";
  text: string;
};

type TrackableOutcome = Extract<
  SubmitOutcome,
  { kind: "started" | "inProgress" }
>;

function formatGeojson(
  geojson: AppleCountCalibrationFeatureCollection,
  buckets: YieldBucket[],
) {
  return geojson.features
    .filter(
      (feature) =>
        feature.properties?.apple_count !== null &&
        !isNaN(feature.properties?.apple_count),
    )
    .map((feature) => {
      const { properties } = feature;
      const categoryIndex =
        findBucket(buckets, properties.apple_count)?.categoryIndex ?? 0;

      return {
        plotId: properties.plot_id,
        yieldCategory: categoryIndex,
        manualCount: null,
        color: calibrationColorVar(categoryIndex),
      };
    });
}

type AppleCountItem = ReturnType<typeof formatGeojson>[number];

function toCalibrationItems(
  measurements: CalibrationDraftMeasurement[],
  appleCountsMap: Map<number, AppleCountItem>,
): CalibrationItem[] {
  return measurements.map((measurement) => {
    const appleCountItem = appleCountsMap.get(measurement.plotId);
    return {
      plotId: measurement.plotId,
      manualCount: measurement.manualCount,
      yieldCategory: appleCountItem?.yieldCategory,
      color: appleCountItem?.color,
    } as CalibrationItem;
  });
}

function toDraftMeasurements(
  calibrationSet: CalibrationItem[],
): CalibrationDraftMeasurement[] {
  return calibrationSet.map(({ plotId, manualCount }) => ({
    plotId,
    manualCount,
  }));
}

interface UseCalibrationProps {
  geojson: AppleCountCalibrationFeatureCollection;
  resultSet: CalibrationData["resultSet"];
  resultSetId?: string;
}

export function useCalibration({
  geojson,
  resultSet,
  resultSetId,
}: UseCalibrationProps) {
  const { t } = useTranslation();

  const buckets = useMemo(
    () =>
      calculateAppleThresholds(geojson.features, {
        bucketCount: CALIBRATION_COLOR_COUNT,
      }),
    [geojson],
  );

  const appleCountsData = useMemo(
    () => formatGeojson(geojson, buckets),
    [geojson, buckets],
  );

  const appleCountsMap = useMemo(
    () => new Map(appleCountsData.map((item) => [item.plotId, item])),
    [appleCountsData],
  );

  const [draft] = useState(() => readCalibrationDraft(resultSetId));

  const [selectedFeatureId, setSelectedFeatureId] = useState<number | null>(
    null,
  );
  const [calibrationSet, setCalibrationSet] = useState<CalibrationItem[]>(() =>
    toCalibrationItems(draft?.measurements ?? [], appleCountsMap),
  );
  const [averageWeight, setAverageWeight] = useState<number | null>(
    draft?.averageWeight ?? null,
  );
  const [draftRestored, setDraftRestored] = useState<boolean>(!!draft);
  const [yieldEstimate, setYieldEstimate] = useState<YieldEstimate>();
  const [lastCalibratedDate, setLastCalibratedDate] = useState<
    string | undefined
  >();

  const [submitNotice, setSubmitNotice] = useState<SubmitNotice | null>(null);
  const [loadingError, setLoadingError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const hasUnsavedChanges = useRef<boolean>(false);

  const selectedPlot = useMemo(() => {
    if (selectedFeatureId === null) return;
    const selectedFeature = appleCountsMap.get(selectedFeatureId);
    if (!selectedFeature) return;

    const calibrationItem = calibrationSet.find(
      (item) => item.plotId === selectedFeatureId,
    );

    return {
      plotId: selectedFeature.plotId,
      yieldCategory: selectedFeature.yieldCategory,
      manualCount: calibrationItem
        ? calibrationItem.manualCount
        : selectedFeature.manualCount,
      color: selectedFeature.color,
    };
  }, [appleCountsMap, selectedFeatureId, calibrationSet]);

  useEffect(() => {
    pruneCalibrationDrafts();
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (!resultSet.calibration) return;

      try {
        const [calibrationData, yieldEstimateData] = await Promise.all([
          getCalibrationMeasurementSet(
            resultSet.calibration.calibration_measurement_set_id,
          ),
          getYieldEstimate(resultSet.id),
        ]);

        if (!isMounted) return;

        setLastCalibratedDate(calibrationData.created_at);
        setYieldEstimate(yieldEstimateData);

        if (hasUnsavedChanges.current) return;

        const staleDraft =
          draft && isDraftStale(draft, calibrationData.created_at);
        if (draft && !staleDraft) return;

        if (staleDraft) {
          clearCalibrationDraft(resultSetId);
          setDraftRestored(false);
        }

        setCalibrationSet(
          toCalibrationItems(
            calibrationData.measurements.map((measurement) => ({
              plotId: measurement.plot_id,
              manualCount: measurement.manual_count,
            })),
            appleCountsMap,
          ),
        );
        setAverageWeight(yieldEstimateData?.average_weight_g ?? null);
      } catch (err) {
        console.error("Failed to load local staging data", err);
        setLoadingError(
          `${t("text.error_occured")}. ${t("text.refresh_page")}`,
        );
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, [resultSet.id, resultSet.calibration, appleCountsMap, draft, resultSetId]);

  useEffect(() => {
    if (!hasUnsavedChanges.current) return;

    writeCalibrationDraft(resultSetId, {
      measurements: toDraftMeasurements(calibrationSet),
      averageWeight,
    });
  }, [calibrationSet, averageWeight, resultSetId]);

  const onLoadingErrorClose = () => {
    setLoadingError("");
  };

  const dismissDraftRestored = () => {
    setDraftRestored(false);
  };

  const selectFeature = (feature: SelectedAppleFeature) => {
    const plotId = feature?.properties?.plot_id;
    if (plotId == null) return;
    setSelectedFeatureId(plotId);
  };

  const clearSelection = () => {
    setSelectedFeatureId(null);
  };

  const removeCalibrationItem = (plotId: number) => {
    hasUnsavedChanges.current = true;
    setCalibrationSet((prev) => prev.filter((s) => s.plotId !== plotId));
    if (selectedFeatureId === plotId) clearSelection();
  };

  const editCalibrationItem = (plotId: number) => {
    setSelectedFeatureId(plotId);
  };

  const updateAverageWeight = (weight: number | null) => {
    hasUnsavedChanges.current = true;
    setAverageWeight(weight);
  };

  const saveCalibrationItem = ({
    selectedPlot,
    manualCount,
  }: {
    selectedPlot: CalibrationItem;
    manualCount: number;
  }) => {
    if (!selectedPlot || manualCount == null) return;

    const updatedCalibrationItem: CalibrationItem = {
      plotId: selectedPlot.plotId,
      manualCount: manualCount,
      yieldCategory: selectedPlot.yieldCategory,
      color: selectedPlot.color,
    };

    hasUnsavedChanges.current = true;

    setCalibrationSet((prev) => {
      const exists = prev.some((item) => item.plotId === selectedPlot.plotId);
      if (exists) {
        return prev.map((item) =>
          item.plotId === selectedPlot.plotId ? updatedCalibrationItem : item,
        );
      }
      return [...prev, updatedCalibrationItem];
    });

    clearSelection();
  };

  const clearDraftAfterSubmit = () => {
    hasUnsavedChanges.current = false;
    clearCalibrationDraft(resultSetId);
  };

  const reloadYieldEstimate = async () => {
    if (!resultSet.calibration) return;
    try {
      setYieldEstimate(await getYieldEstimate(resultSet.id));
    } catch (error) {
      console.error("Failed to reload the yield estimate", error);
    }
  };

  const submitCalibration = async (
    weight: number,
  ): Promise<TrackableOutcome | null> => {
    if (!resultSetId) return null;

    setLoading(true);
    setSubmitNotice(null);

    let response: Response | null = null;
    try {
      response = await createCalibrationSet(
        calibrationSet,
        resultSetId,
        weight,
      );
    } catch (error) {
      // Deliberate: RUM collects console.error, so a submit the network ate is
      // still counted even though the grower is shown an error notice instead.
      console.error("Failed to submit calibration", error);
    }

    const outcome = await readSubmitOutcome(response);
    setLoading(false);

    switch (outcome.kind) {
      case "started":
        clearDraftAfterSubmit();
        return outcome;
      case "inProgress":
        return outcome;
      case "yieldUpdated":
        clearDraftAfterSubmit();
        setSubmitNotice({
          severity: "success",
          text: t("message.calibration.yield_updated"),
        });
        await reloadYieldEstimate();
        return null;
      case "failed":
        setSubmitNotice({
          severity: "error",
          text: t(failureMessageKey(outcome.errorCode)),
        });
        return null;
      case "notStarted":
        setSubmitNotice({
          severity: "error",
          text: t("message.calibration.not_started"),
        });
        return null;
      default:
        setSubmitNotice({
          severity: "error",
          text: `${t("text.error_occured")}. ${t("text.try_again")}.`,
        });
        return null;
    }
  };

  return {
    state: {
      calibrationSet,
      averageWeight,
      yieldEstimate,
      lastCalibratedDate,
      submitNotice,
      loadingError,
      loading,
      selectedFeatureId,
      selectedPlot,
      draftRestored,
    },
    derived: {
      buckets,
    },
    actions: {
      selectFeature,
      clearSelection,
      removeCalibrationItem,
      editCalibrationItem,
      saveCalibrationItem,
      submitCalibration,
      setAverageWeight: updateAverageWeight,
      onLoadingErrorClose,
      dismissDraftRestored,
    },
  };
}
